import { FC, PropsWithChildren, createContext, useCallback, useContext, useEffect, useState } from 'react';
import * as GradeAPI from 'services/unit-grades';
import * as StatusAPI from 'services/unit-statuses';
import { UnitLookupOption } from 'types';

type LookupContextType = {
  /* active labels, pre-sorted — drop-in for the old Object.values(enum) dropdown sources */
  unitStatusOptions: string[];
  unitGradeOptions: string[];
  /* full rows incl. inactive — admin grids */
  unitStatusList: UnitLookupOption[];
  unitGradeList: UnitLookupOption[];
  saveUnitStatus: (option: Partial<UnitLookupOption>) => Promise<void>;
  deleteUnitStatus: (id: number) => Promise<void>;
  saveUnitGrade: (option: Partial<UnitLookupOption>) => Promise<void>;
  deleteUnitGrade: (id: number) => Promise<void>;
};

export const LookupContext = createContext<LookupContextType>({
  unitStatusOptions: [],
  unitGradeOptions: [],
  unitStatusList: [],
  unitGradeList: [],
  saveUnitStatus: async () => {},
  deleteUnitStatus: async () => {},
  saveUnitGrade: async () => {},
  deleteUnitGrade: async () => {}
});

export const useLookups = () => useContext(LookupContext);

export const LookupProvider: FC<PropsWithChildren> = ({ children }) => {
  const [unitStatusOptions, setUnitStatusOptions] = useState<string[]>([]);
  const [unitGradeOptions, setUnitGradeOptions] = useState<string[]>([]);
  const [unitStatusList, setUnitStatusList] = useState<UnitLookupOption[]>([]);
  const [unitGradeList, setUnitGradeList] = useState<UnitLookupOption[]>([]);

  const refreshStatuses = useCallback(async () => {
    setUnitStatusOptions(await StatusAPI.getUnitStatusOptions());
    setUnitStatusList(await StatusAPI.getUnitStatusList());
  }, []);

  const refreshGrades = useCallback(async () => {
    setUnitGradeOptions(await GradeAPI.getUnitGradeOptions());
    setUnitGradeList(await GradeAPI.getUnitGradeList());
  }, []);

  useEffect(() => {
    refreshStatuses();
    refreshGrades();
  }, [refreshStatuses, refreshGrades]);

  const saveUnitStatus = useCallback(
    async (option: Partial<UnitLookupOption>) => {
      if (option.id) await StatusAPI.updateUnitStatus(option as UnitLookupOption);
      else await StatusAPI.saveUnitStatus(option);
      await refreshStatuses();
    },
    [refreshStatuses]
  );

  const deleteUnitStatus = useCallback(
    async (id: number) => {
      await StatusAPI.deleteUnitStatus(id);
      await refreshStatuses();
    },
    [refreshStatuses]
  );

  const saveUnitGrade = useCallback(
    async (option: Partial<UnitLookupOption>) => {
      if (option.id) await GradeAPI.updateUnitGrade(option as UnitLookupOption);
      else await GradeAPI.saveUnitGrade(option);
      await refreshGrades();
    },
    [refreshGrades]
  );

  const deleteUnitGrade = useCallback(
    async (id: number) => {
      await GradeAPI.deleteUnitGrade(id);
      await refreshGrades();
    },
    [refreshGrades]
  );

  return (
    <LookupContext.Provider
      value={{
        unitStatusOptions,
        unitGradeOptions,
        unitStatusList,
        unitGradeList,
        saveUnitStatus,
        deleteUnitStatus,
        saveUnitGrade,
        deleteUnitGrade
      }}
    >
      {children}
    </LookupContext.Provider>
  );
};
